/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-11-16 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Card,
  DataTable,
  SkeletonDisplayText,
  Pagination,
  Icon,
  TextStyle,
  Tooltip,
  TextField
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { CaretDownMinor, CaretUpMinor, SearchMinor } from '@shopify/polaris-icons';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import Filter from 'src/components/containers/analytics-v2/filter';
import {
  QUERY_EXECUTION_KEY,
  TOP_ITEM_IN_DETAIL,
  NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
  MAP_SORT_VALUE,
  SORT_VALUE,
  RECOMMENDATION_ANALYTIC_METRIC,
  FEATURES
} from 'src/constants/analytics';
import { formatNumberInThousand, optimizeImage } from 'src/helpers';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import PopoverActionButton from 'src/components/views/analytics-v2/popover-action-button';
import noProductImage from 'src/assets/images/product/no-product-image.png';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {
  perPage: number,
  page: number,
  hasNext: boolean,
  hasPrevious: boolean,
  data: any[],
  totalPage: number,
  directionSort: string
};

const fromDateStateName = 'recommendationAnalyticsFromDate';
const toDateStateName = 'recommendationAnalyticsToDate';
const compareStateName = 'recommendationAnalyticsCompare';

const orderHeaders = ['', 'productName', 'revenue', 'orderCount', 'conversionRate', 'clickCount'];

// {
//   "productName": "Macbook Pro 16 inch (2020 ) For Sale 0",
//   "revenue": 112738,
//   "orderCount": 7502,
//   "conversionRate": 23.3,
//   "clickCount": 331302,
//   "productImages": {
//     "1": "https://cdn.shopify.com/s/files/1/0563/1770/6323/products/AELITAMIDIDRESS.webp?v=1673492206"
//   }
// }

const filterProductBySearch = (arr, currentKeySearch) => {
  const arrFiltered = arr.filter(product => {
    if (product.productName?.toLowerCase().includes(currentKeySearch.toLowerCase())) return true;
    return false;
  });
  return arrFiltered;
};

class CollectionPerformanceAnalytics extends Component<ComposedProps, State> {
  state = {
    perPage: 10,
    hasPrevious: false,
    hasNext: false,
    data: [],
    page: 1,
    totalPage: 0,
    directionSort: 'desc',
    currentKeySearch: '',
    recommendationTopProductPerformanceOrdered: this.props.recommendationTopProductPerformance
  };

  componentDidMount() {
    const { recommendationTopProductPerformance, fromDate, toDate, compare } = this.props;
    const { perPage } = this.state;

    const hasNext = _.size(recommendationTopProductPerformance) > perPage;
    this.setState({ hasNext });

    if (fromDate && toDate) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);
      this.getRecommendationTopProduct(fromDateUTC, toDateUTC, compare);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate, compare } = this.props;
    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationTopProductPerformance: nextRecommendationTopProductPerformance
    } = nextProps;
    const { currentKeySearch } = this.state;
    const dataFilteredAndSorted = filterProductBySearch(
      nextRecommendationTopProductPerformance,
      currentKeySearch
    );

    this.setState({
      recommendationTopProductPerformanceOrdered: dataFilteredAndSorted
    });

    // Check changing filter
    if (fromDate !== nextFromDate || toDate !== nextToDate || compare !== nextCompare) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0, 0);

      // Reset page when change date filter
      this.setState({
        page: 1
      });

      onAbortApiAnalytic();
      setTimeout(() => {
        this.getRecommendationTopProduct(fromDateUTC, toDateUTC, nextCompare);
      }, 500);
    }

    if (!_.isEmpty(dataFilteredAndSorted)) {
      const newTotalPage = Math.ceil(
        dataFilteredAndSorted.length / NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      );
      this.setState({
        totalPage: newTotalPage
      });
    }

    this.paginateData(this.state.page, dataFilteredAndSorted);

    // Update execution ids
    this.processExecutionId_RecommendationTopProductPerformance(this.props, nextProps);
  }

  paginateData = (page, data) => {
    this.setState({
      data: data.slice(
        (page - 1) * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
        page * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      )
    });
  };

  buildOptions = () => {
    const { recommendationTopProductPerformanceLoading, tenantApp } = this.props;
    const { data } = this.state;
    const result = [];
    if (recommendationTopProductPerformanceLoading) {
      [...Array(5)].forEach(() =>
        result.push([
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />,
          <SkeletonDisplayText size="small" />
        ])
      );
    } else {
      data.forEach(item => {
        const { clickCount, conversionRate, orderCount, productName, revenue, productImages } =
          item;
        const firstImageIndex = !_.isEmpty(productImages) ? Object.keys(productImages)[0] : null;
        let image = !firstImageIndex
          ? noProductImage
          : optimizeImage(productImages[firstImageIndex], '40x40');
        result.push([
          <div className={`product-image ${_.isEmpty(productImages) ? 'no-image' : ''}`}>
            <img src={image} alt={productName} />
          </div>,
          <div>
            <span>{productName}</span>
          </div>,
          <div className="cell-value-table">{formatNumberInThousand(revenue)}</div>,
          <div className="cell-value-table">{formatNumberInThousand(orderCount)}</div>,
          <div className="cell-value-table">{formatNumberInThousand(conversionRate)}%</div>,
          <div className="cell-value-table">{formatNumberInThousand(clickCount)}</div>,
          // <div className="cell-value-table">xx</div>
          <PopoverActionButton product={item} itemsMenu={['merchandising']} tenantApp={tenantApp} />
        ]);
      });
    }
    return result;
  };

  handleSort = (headingIndex: number, direction: string) => {
    const { sortData } = this.props;
    this.setState({
      directionSort: MAP_SORT_VALUE[direction]
    });

    sortData(
      'recommendationTopProductPerformance',
      orderHeaders[headingIndex],
      MAP_SORT_VALUE[direction]
    );
  };

  nextPage = () => {
    const { page, totalPage, recommendationTopProductPerformanceOrdered } = this.state;
    if (page === totalPage) {
      return;
    }

    this.paginateData(page + 1, recommendationTopProductPerformanceOrdered);
    this.setState({
      page: page + 1
    });
  };

  previousPage = () => {
    const { page, recommendationTopProductPerformanceOrdered } = this.state;
    if (page === 1) {
      return;
    }

    this.paginateData(page - 1, recommendationTopProductPerformanceOrdered);
    this.setState({
      page: page - 1
    });
  };

  getRecommendationTopProduct = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_PRODUCT_PERFORMANCE;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getRecommendationTopProduct(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  processExecutionId_RecommendationTopProductPerformance = (props, nextProps) => {
    const { recommendationTopProductPerformance_QueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationTopProductPerformance_QueryExecutionId:
        recommendationTopProductPerformance_QueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(recommendationTopProductPerformance_QueryExecutionIdNext) &&
      recommendationTopProductPerformance_QueryExecutionId !==
        recommendationTopProductPerformance_QueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_PRODUCT_PERFORMANCE,
        executionId: recommendationTopProductPerformance_QueryExecutionIdNext
      });
    }
  };

  updateCurrentKeySearch = currentKeySearch => {
    const { recommendationTopProductPerformance } = this.props;
    this.setState({ page: 1 });
    const dataFilteredAndSorted = filterProductBySearch(
      recommendationTopProductPerformance,
      currentKeySearch
    );

    this.setState({ currentKeySearch, data: dataFilteredAndSorted });
    if (!_.isEmpty(dataFilteredAndSorted)) {
      const newTotalPage = Math.ceil(
        dataFilteredAndSorted.length / NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      );
      this.setState({
        totalPage: newTotalPage
      });
    }

    this.paginateData(1, dataFilteredAndSorted);
  };

  render() {
    const {
      i18n,
      recommendationTopProductPerformanceLoading,
      openReviewAddonModal,
      performActionSurvey
    } = this.props;

    const {
      page,
      directionSort,
      totalPage,
      currentKeySearch,
      recommendationTopProductPerformanceOrdered
    } = this.state;
    const options = this.buildOptions();
    const arrowSort = directionSort === SORT_VALUE.ASCENDING ? CaretUpMinor : CaretDownMinor;

    const dataFilteredAndSorted = filterProductBySearch(
      recommendationTopProductPerformanceOrdered,
      currentKeySearch
    );

    return (
      <div
        className="BC-Page BC-Page__RecommendationAnalytics__Top__Product"
        onScroll={this.handleScroll}>
        <Filter
          feature={FEATURES.RECOMMENDATION}
          performActionSurvey={performActionSurvey}
          fromDateStateName={fromDateStateName}
          toDateStateName={toDateStateName}
          compareStateName={compareStateName}
          metric={RECOMMENDATION_ANALYTIC_METRIC.TOP_PRODUCT_PERFORMANCE}
          showCompare={false}
          openReviewAddonModal={openReviewAddonModal}
        />

        <div>
          <Card>
            <Card.Section>
              <div className="Polaris-Card__Header action-header">
                <Tooltip
                  preferredPosition="above"
                  content={i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendation_Tooltip_TopProduct_cardTitle'
                  )}>
                  <TextStyle>
                    <h2 className="Polaris-Heading">
                      {i18n.translate(
                        'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_cardTitle'
                      )}
                    </h2>
                  </TextStyle>
                </Tooltip>
                <TextField
                  onChange={value => this.updateCurrentKeySearch(value)}
                  value={currentKeySearch}
                  prefix={<Icon source={SearchMinor} color="base" />}
                  placeholder={i18n.translate('AdminHTML.Common.Action.search')}
                />
              </div>
            </Card.Section>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
              sortable={[true, true, true, true, true, true, false]}
              onSort={this.handleSort}
              defaultSortDirection="descending"
              footerContent={
                _.isEmpty(dataFilteredAndSorted) && !recommendationTopProductPerformanceLoading
                  ? i18n.translate(
                      'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_noResult',
                      { count: 0 }
                    )
                  : ''
              }
              headings={[
                <div></div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_header_product'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_header_revenue'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_header_order_count'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_header_conversion_rate'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_header_click_count'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_header_action'
                  )}
                </div>
              ]}
              rows={options}
            />
          </Card>
          <div className="BC-Page__RecommendationAnalytics__Top__Product__Pagination">
            {!_.isEmpty(dataFilteredAndSorted) && !recommendationTopProductPerformanceLoading && (
              <Pagination
                label={`${page}/${totalPage}`}
                hasPrevious={page > 1}
                onPrevious={this.previousPage}
                hasNext={page < totalPage}
                onNext={this.nextPage}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getRecommendationTopProduct: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getRecommendationTopProduct(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  sortData: (name, field, direction, fieldInsensitive = '') =>
    dispatch(AnalyticsActions.sortData(name, field, direction, fieldInsensitive)),

  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  return {
    tenantApp: _.get(state, 'oauth.tenantApp', {}),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),

    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    // Collection: top
    recommendationTopProductPerformance: _.get(
      state.analyticsV2,
      'recommendationTopProductPerformance',
      []
    ),
    recommendationTopProductPerformanceLoading: _.get(
      state.analyticsV2,
      'recommendationTopProductPerformanceLoading',
      false
    ),
    recommendationTopProductPerformance_QueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationTopProductPerformanceQueryExecutionId',
      ''
    )
  };
};

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(CollectionPerformanceAnalytics)
);
